// Copyright (c) Ping. All rights reserved.
//
// Web host for the redesigned shared NewChat modal (v2). Mirrors the
// desktop host (`apps/desktop/src/features/chat/NewChatModal.tsx`)
// one-for-one — the difference is the BE-client + persistence
// modules.
//
// `NewChatModal` is presentation-only — it owns the debounced query
// state and the result list, then delegates two async callbacks:
//
//   onSearch(query, signal) → DirectoryUser[]
//     `lookupUser` against GET /v1/users/lookup. Returns 0 or 1
//     result (exact-match on the account's primary 3PID).
//   onPick(user) → conversationId
//     1. claimContact(identifier)  — POST /v1/contacts/lookup;
//        resolves the account AND claims one KeyPackage atomically.
//     2. retrieveIdentity()         — inviter snapshot.
//     3. getOrCreateMlsConversation — mint or recover MLS group;
//        the pre-claimed device_id + key_package short-circuit the
//        old per-user devices/key-package fan-out.
//     4. ensureConversation         — local persistence row.
//     5. send profile-intro payload — receiver UI hydration.
//
// The dead `POST /v1/conversations/request` call is gone — the
// canonical BE no longer exposes that route. `POST /v1/contacts/lookup`
// is the start-of-chat trigger.

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  encodeEnvelopeJsonBytes,
  NewChatModal as NewChatModalUi,
  type NewChatModalDirectoryUser,
  pingToast,
  useSession,
} from '@ping/ui';

import { log } from '@core/log/log';
import { claimAllDevicesByAccountId, type DirectoryUser,lookupUser } from '@core/network';
import { getUserProfile , searchUsers } from '@core/network/users';
import { retrieveIdentity } from '@core/persistence/identity';
import { encodeGroupJoinRequest } from '@features/chat/groupInvite';
import { buildUserInviteLink } from '@features/chat/inviteLink';
import {
  type DeviceContact,
  filterDeviceContacts,
  loadDeviceContacts,
} from '@features/contacts/deviceContacts';
import { useSelfProfile } from '@features/profile/selfProfileStore';
import { getCurrentTransport } from '@session/sessionBootstrap';

import { ensureConversation } from './ensureConversation';
import { getOrCreateMlsConversation } from './mlsConversation';

// Map a stored device contact → the modal's directory-user shape, tagged
// `source: 'contact'` so it renders under "YOUR CONTACTS". `id`/`username`
// carry the email/phone identifier — resolved to a Ping account on pick.
function contactToDirectoryUser(c: DeviceContact): NewChatModalDirectoryUser {
  return {
    id: `contact:${c.id}`,
    username: c.identifier,
    displayName: c.displayName,
    source: 'contact',
    ...(c.avatarUri !== undefined ? { avatarUri: c.avatarUri } : {}),
  };
}

// Heuristic: does the query look like an email / phone (→ exact lookup)?
function looksLikeContact(q: string): boolean {
  return /@/.test(q) || /^\+?[0-9][0-9 ()-]{5,}$/.test(q);
}

// Wire-format we pack into the MLS conversation's `name` field so the
// receiver can decode the inviter's profile on Welcome. Mirrors the
// desktop file's encoding exactly so receivers can decode either
// side's invites with the same parser.
const CONV_META_PREFIX = 'ping:meta:v1:';

interface ConvMeta {
  inviter: { accountId: string; displayName: string };
  peer: { accountId: string; displayName: string };
}

export interface NewChatModalProps {
  readonly open: boolean;
  readonly onDismiss: () => void;
  readonly onConversationStarted: (conversationId: string) => void;
  /** When set (deep-link `/u/<accountId>`), auto-resolve + start a 1-1 with this
   *  account on open — no manual search/pick needed. */
  readonly autoStartAccountId?: string | undefined;
  /** When set together with `autoStartAccountId` (deep-link `/g/<approver>/<groupId>`),
   *  after the 1-1 starts, send a group-join REQUEST to that account so they can
   *  approve adding the opener to the group. */
  readonly autoStartGroupJoin?: { readonly groupId: string } | undefined;
}

export function NewChatModal(props: NewChatModalProps): JSX.Element {
  const { open, onDismiss, onConversationStarted, autoStartAccountId, autoStartGroupJoin } = props;
  const { client } = useSession();
  const [shareLink, setShareLink] = useState<string | undefined>(undefined);
  const [deviceContacts, setDeviceContacts] = useState<readonly DeviceContact[]>([]);

  // Load this device's saved contacts whenever the modal opens (vCard
  // import / Contact Picker populate them via the /contacts screen).
  useEffect(() => {
    if (open) setDeviceContacts(loadDeviceContacts());
  }, [open]);

  // The user's own shareable 1-1 invite link — a RESOLVABLE URL encoding their
  // account id (the recipient's client resolves it + starts a chat). This is
  // what gets copied, so it must be the real link (a pretty `ping.app/handle`
  // would need a backend handle resolver, which doesn't exist yet).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      const id = await retrieveIdentity();
      if (!cancelled && id !== null && id.accountId.length > 0) {
        setShareLink(buildUserInviteLink(id.accountId));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSearch = useCallback(
    async (query: string, signal: AbortSignal): Promise<readonly NewChatModalDirectoryUser[]> => {
      const trimmed = query.trim();
      // YOUR CONTACTS — this device's saved contacts (all when empty, else
      // filtered). Always shown so the empty-query state lists contacts.
      const contactRows = filterDeviceContacts(deviceContacts, trimmed).map(
        contactToDirectoryUser,
      );
      if (trimmed.length === 0) return contactRows;
      // ON PING — directory NAME search + exact email/phone lookup.
      const [dir, exact] = await Promise.all([
        searchUsers({ query: trimmed, limit: 20, signal, skipReauth: true })
          .then((r) => r.users)
          .catch(() => [] as DirectoryUser[]),
        looksLikeContact(trimmed)
          ? lookupUser({ identifier: trimmed, signal })
              .then((r) => (r.user !== null ? [r.user] : []))
              .catch(() => [] as DirectoryUser[])
          : Promise.resolve([] as DirectoryUser[]),
      ]);
      const byId = new Map<string, NewChatModalDirectoryUser>();
      for (const c of contactRows) byId.set(c.id, c);
      for (const u of [...exact, ...dir]) {
        if (byId.has(u.id)) continue;
        byId.set(u.id, { ...(u as NewChatModalDirectoryUser), source: 'on-ping' });
      }
      return Array.from(byId.values());
    },
    [deviceContacts],
  );

  const handlePick = useCallback(
    async (picked: NewChatModalDirectoryUser): Promise<string> => {
      if (client === null) {
        throw new Error('Still signing you in. Try again in a moment.');
      }
      // A "YOUR CONTACTS" row carries an email/phone, not an account id —
      // resolve it to a real Ping account first (or surface a friendly
      // "not on Ping yet" message).
      let user = picked;
      if (picked.source === 'contact') {
        const { user: resolved } = await lookupUser({ identifier: picked.username });
        if (resolved === null) {
          throw Object.assign(
            new Error(`${picked.displayName} isn't on Ping yet — invite them to chat.`),
            { serverMessage: `${picked.displayName} isn't on Ping yet — invite them to chat.` },
          );
        }
        user = resolved as NewChatModalDirectoryUser;
      }
      // Multi-device fan-out claim: `POST /v1/contacts/claim-fanout`
      // atomically consumes one KP per active device on the peer
      // account in a single round-trip. Per docs/architecture/multi-device.md
      // "Per-device fan-out" — each peer device's MLS leaf is added
      // with a distinct single-use KP, satisfying RFC 9420 §16.4.
      // Two concurrent inviters can't receive the same KP for the
      // same device thanks to the SQL-level `FOR UPDATE SKIP LOCKED`
      // — that's the property that previously broke when this path
      // used the LIST endpoint (`GET /v1/devices/{accountId}`).
      const claimed = await claimAllDevicesByAccountId({ accountId: user.id });

      const identity = await retrieveIdentity();
      const meta: ConvMeta = {
        inviter: {
          accountId: identity?.accountId ?? '',
          displayName: identity?.displayName ?? 'Someone',
        },
        peer: {
          accountId: claimed.accountId,
          displayName: (user as DirectoryUser).displayName,
        },
      };

      // Mint (or recover) the MLS group. The atomic claim above already
      // gave us the peer's device_id + key_package bytes, so pass them
      // through — the orchestration helper skips its own per-user
      // device/KP fan-out when they're supplied.
      const result = await getOrCreateMlsConversation({
        client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
        transport: getCurrentTransport(),
        // Without a separate /v1/conversations/request id we key the
        // local conversation off the MLS group bytes themselves —
        // `getOrCreateMlsConversation` uses the `beConversationId`
        // arg as a cache key but otherwise carries no semantics, so
        // a deterministic per-account string is fine.
        beConversationId: `acct:${claimed.accountId}`,
        conversationName: `${CONV_META_PREFIX}${JSON.stringify(meta)}`,
        log,
        peerAccountId: claimed.accountId,
        preClaimedPeers: claimed.devices.map((d) => ({
          deviceIdHex: d.deviceId,
          keyPackageBytes: d.keyPackage,
        })),
      });
      const localConvId = result.conversationIdHex;

      // Insert/upsert the local conversation row keyed by the MLS id.
      // Without this, ChatRoom mounted on the returned id finds
      // nothing in useMessages / useConversations and renders empty.
      await ensureConversation({
        conversationId: localConvId,
        counterpartDisplayName: (user as DirectoryUser).displayName,
        kind: 'direct',
        peerAccountId: claimed.accountId,
      });

      // Subscribe the new 1:1 for realtime immediately so the initiator receives
      // the peer's first reply without waiting for the next `App.tsx` reconcile
      // pass (a brand-new conversation has no live WS subscription until then).
      // Idempotent + best-effort.
      void getCurrentTransport()
        ?.subscribeToConversations([localConvId])
        .catch(() => undefined);

      // A FAILED invite means no Welcome reached the peer — the chat would
      // open but nothing the user sends would ever be delivered. Surface it
      // instead of silently navigating into a dead conversation. The local
      // row from `ensureConversation` above is kept so a retry (next open,
      // once online / once they replenish keys) recovers cleanly via
      // `getOrCreateMlsConversation` (the invited flag stays unset).
      // 'skipped' (already invited / existing chat) is success — fall through.
      if (result.inviteStatus.kind === 'failed') {
        const friendly =
          result.inviteStatus.reason === 'peer_pool_empty'
            ? `${(user as DirectoryUser).displayName} can't receive invites right now — ask them to open Ping, then try again.`
            : result.inviteStatus.reason === 'no_peer_devices'
              ? `${(user as DirectoryUser).displayName} hasn't set up Ping on any device yet.`
              : result.inviteStatus.reason === 'no_transport'
                ? 'You appear to be offline — reconnect and try again.'
                : `Couldn't start the chat with ${(user as DirectoryUser).displayName}. Please try again.`;
        log.warn({
          event: 'newChat.invite.failed',
          accountId: claimed.accountId,
          reason: result.inviteStatus.reason,
        });
        throw Object.assign(new Error(friendly), { serverMessage: friendly });
      }

      // Profile-intro after the Welcome lands. The receiver's
      // useIncomingMessages hook recognises kind="ping.profile" and
      // updates the conv row's display name + peerAccountId instead
      // of rendering a bubble. MLS `Conversation::join` clears
      // `meta.name`, so we re-send the inviter's identity as the
      // first application message.
      if (result.inviteStatus.kind === 'ok') {
        try {
          // Include our own avatar media id so the receiver renders our
          // picture immediately on join — without it the peer sees our name
          // but a blank/initials avatar until a later profile re-send. The
          // receiver resolves it via the media URL resolver (see the
          // `direct: true` branch in `useIncomingMessages`).
          const selfAvatarMediaId = useSelfProfile.getState().avatarMediaId;
          const profile = {
            kind: 'ping.profile',
            payload: {
              accountId: meta.inviter.accountId,
              displayName: meta.inviter.displayName,
              ...(selfAvatarMediaId !== null && selfAvatarMediaId.length > 0
                ? { avatarMediaId: selfAvatarMediaId }
                : {}),
              // 1:1 marker — the receiver names the row after the
              // sender (the other party) and never after itself. See
              // the `direct: true` branch in `useIncomingMessages`.
              direct: true,
            },
          };
          await result.conv.send(new TextEncoder().encode(JSON.stringify(profile)));
          log.info({
            event: 'newChat.profileIntro.sent',
            localConvId,
          });
        } catch (profileErr) {
          log.warn({
            event: 'newChat.profileIntro.failed',
            error: (profileErr as Error).message,
          });
        }
      }
      log.info({
        event: 'newChat.claimContact.ok',
        accountId: claimed.accountId,
        inviteStatus: result.inviteStatus.kind,
      });

      return localConvId;
    },
    [client],
  );

  // Deep-link auto-start: opened via `/u/<accountId>` (1-1) or
  // `/g/<approver>/<groupId>` (group). Resolve the target and run the normal
  // start-1-1 flow once; for a group link, also send a join request afterward.
  const autoStartedRef = useRef<string | null>(null);
  useEffect(() => {
    // Runs HEADLESS — it does NOT require the modal to be open. An invite deep
    // link sets `autoStartAccountId` WITHOUT opening the visible new-chat modal,
    // so the 1-1 starts (and any group-join request sends) silently in the
    // background, then we navigate to the conversation.
    if (autoStartAccountId === undefined || autoStartAccountId.length === 0) return;
    // Wait for the SDK client — on a fresh navigation to the link the session is
    // still bootstrapping. Return early WITHOUT marking the ref so the effect
    // re-runs (client is a dep) and starts once the client is live. (Marking the
    // ref before this was the bug: the first attempt failed with a null client
    // and never retried → the modal just sat there.)
    if (client === null) return;
    if (autoStartedRef.current === autoStartAccountId) return;
    autoStartedRef.current = autoStartAccountId;
    // NOTE: no `cancelled` cleanup flag. React 18 StrictMode double-invokes
    // effects (run → cleanup → run); a cancelled flag set by the first cleanup
    // made the (only, ref-guarded) async bail before doing anything, so nothing
    // happened. The `autoStartedRef` guard already prevents duplicate/looping
    // runs and persists across the StrictMode double-invoke, so we let the async
    // run to completion.
    void (async () => {
      try {
        // Self-link guard: you can't 1-1 / request-to-join yourself.
        const me = await retrieveIdentity();
        if (me?.accountId === autoStartAccountId) {
          pingToast.info({
            title: autoStartGroupJoin !== undefined ? 'Your own group link' : 'Your own link',
            description: 'Share it with someone else to invite them.',
          });
          onDismiss();
          return;
        }
        const profile = await getUserProfile(autoStartAccountId).catch(() => null);
        const convId = await handlePick({
          id: autoStartAccountId,
          username: autoStartAccountId,
          displayName:
            profile?.displayName !== undefined && profile.displayName.length > 0
              ? profile.displayName
              : 'Ping user',
          source: 'on-ping',
        });
        // Group invite link: after the 1-1 with the approver exists, send the
        // join REQUEST (drives their Approve banner) + a friendly visible message
        // so the 1-1 isn't empty and the approver has context.
        if (autoStartGroupJoin !== undefined) {
          try {
            const result = await getOrCreateMlsConversation({
              client: client as unknown as Parameters<typeof getOrCreateMlsConversation>[0]['client'],
              transport: getCurrentTransport(),
              beConversationId: `acct:${autoStartAccountId}`,
              conversationName: undefined,
              log,
            });
            await result.conv.send(
              encodeGroupJoinRequest({
                groupId: autoStartGroupJoin.groupId,
                ...(me?.displayName !== undefined && me.displayName.length > 0
                  ? { requesterDisplayName: me.displayName }
                  : {}),
              }),
            );
            await result.conv.send(
              encodeEnvelopeJsonBytes({
                kind: 'com.ping.text',
                payload: { body: '👋 I’d like to join your group.' },
              }),
            );
            pingToast.success({
              title: 'Request sent',
              description: 'They’ll add you to the group once they approve.',
            });
            log.info({ event: 'newChat.groupJoinRequest.sent' });
          } catch (joinErr) {
            log.warn({ event: 'newChat.groupJoinRequest.failed', err: String(joinErr) });
          }
        }
        onConversationStarted(convId);
      } catch (err) {
        // Don't reset the ref here: the effect's deps include unstable inline
        // callbacks (re-created each parent render), so a reset would re-fire +
        // spam claims every render. A transient failure is recovered by
        // re-opening the link (fresh load → fresh ref).
        log.warn({ event: 'newChat.autoStart.failed', err: String(err) });
      }
    })();
  }, [autoStartAccountId, autoStartGroupJoin, client, handlePick, onConversationStarted, onDismiss]);

  return (
    <NewChatModalUi
      open={open}
      onDismiss={onDismiss}
      onSearch={handleSearch}
      onPick={handlePick}
      onConversationStarted={onConversationStarted}
      {...(shareLink !== undefined ? { shareLink } : {})}
    />
  );
}
